import React from 'react';
import { FullProject, Task } from '../../../types';
import { areProjectIdsMatching, isMatchingTaskId } from '../../../utils/idNormalizer';

export interface ProjectSyncRefs {
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  rawProjectsRef?: React.MutableRefObject<FullProject[]>;
  rawTasksRef?: React.MutableRefObject<Task[]>;
  ownedTasksRef?: React.MutableRefObject<Task[]>;
  dirtyTaskIdsRef?: React.MutableRefObject<Set<string>>;
  recentlyDeletedTaskIdsRef?: React.MutableRefObject<Set<string>>;
}

export interface ProjectSyncTracker {
  readonly refs: ProjectSyncRefs;
  getAllProjects(): FullProject[];
  setAllProjectsRef(projects: FullProject[]): void;
  getRawTasks(): Task[];
  getOwnedTasks(): Task[];
  setRawAndOwnedTasks(rawTasks: Task[], ownedTasks: Task[]): void;
  commitProjects(
    updatedProjects: FullProject[],
    setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void,
    modifiedProjectIds?: Iterable<string>
  ): void;
  syncModifiedProjects(updatedProjects: FullProject[], modifiedProjectIds: Iterable<string>): void;
  syncAllProjects(updatedProjects: FullProject[]): void;
  syncTask(updatedTask: Task, projectId?: string, appendIfMissing?: boolean): void;
  syncMultipleTasks(updatedTasks: Task[], appendIfMissing?: boolean): void;
  replaceProjectTasks(projectId: string, newTasks: Task[]): void;
  recordRecentlyDeleted(taskIds: Iterable<string | number>, ttlMs?: number): void;
  clearRecentlyDeleted(taskIds: Iterable<string | number>): void;
  recordDirtyTasks(taskIds: Iterable<string | number>): void;
}

/**
 * Phase 3 · Schritt 3.3: Central synchronization ref tracker.
 * Ensures that optimistic updates to `allProjectsRef`, `rawProjectsRef`,
 * `rawTasksRef`, `ownedTasksRef`, `dirtyTaskIdsRef`, and `recentlyDeletedTaskIdsRef`
 * happen atomically and with structural sharing.
 */
export function syncModifiedProjectsInRawRef(
  updatedProjects: FullProject[],
  modifiedProjectIds: Iterable<string>,
  rawProjectsRef?: React.MutableRefObject<FullProject[]>
): void {
  if (!rawProjectsRef) return;
  const idList = Array.from(modifiedProjectIds).filter(Boolean);
  if (idList.length === 0) return;

  let changed = false;
  const nextRaw = rawProjectsRef.current.map((rp) => {
    const rawKey = rp.rawId || rp.id;
    const isModified = idList.some(
      (modId) => areProjectIdsMatching(modId, rawKey) || areProjectIdsMatching(modId, rp.id)
    );
    if (!isModified) return rp;

    const updated = updatedProjects.find(
      (up) => areProjectIdsMatching(up.rawId || up.id, rawKey) || areProjectIdsMatching(up.id, rp.id)
    );
    if (!updated || updated === rp) return rp;
    changed = true;
    return { ...updated, id: rp.id, rawId: rp.rawId || rp.id };
  });

  if (changed) {
    rawProjectsRef.current = nextRaw;
  }
}

/**
 * Synchronizes any project in `rawProjectsRef` whose tasks or metadata reference changed
 * in `updatedProjects`, while preserving strict reference equality (`===`) for untouched projects.
 */
export function syncAllProjectsInRawRef(
  updatedProjects: FullProject[],
  rawProjectsRef?: React.MutableRefObject<FullProject[]>
): void {
  if (!rawProjectsRef) return;
  let changed = false;
  const nextRaw = rawProjectsRef.current.map((rp) => {
    const rawKey = rp.rawId || rp.id;
    const updated = updatedProjects.find(
      (up) => areProjectIdsMatching(up.rawId || up.id, rawKey) || areProjectIdsMatching(up.id, rp.id)
    );
    if (!updated) return rp;
    if (
      updated.tasks === rp.tasks &&
      updated.metadata === rp.metadata &&
      updated.lastModified === rp.lastModified
    ) {
      return rp;
    }
    changed = true;
    return { ...updated, id: rp.id, rawId: rp.rawId || rp.id };
  });

  if (changed) {
    rawProjectsRef.current = nextRaw;
  }
}

/**
 * Atomically updates or appends a single mutated task in `rawTasksRef` and `ownedTasksRef`.
 */
export function syncTaskInRawRefs(
  updatedTask: Task,
  projectId: string | undefined,
  rawTasksRef?: React.MutableRefObject<Task[]>,
  ownedTasksRef?: React.MutableRefObject<Task[]>,
  appendIfMissing = false
): void {
  const clone = { ...updatedTask };
  const updateList = (list: Task[]): Task[] => {
    const idx = list.findIndex(
      (rt) =>
        String(rt.id) === String(updatedTask.id) ||
        isMatchingTaskId(updatedTask.id, rt.id, projectId || rt.projectId)
    );
    if (idx !== -1) {
      const next = [...list];
      next[idx] = clone;
      return next;
    }
    return appendIfMissing ? [...list, clone] : list;
  };

  if (rawTasksRef) {
    rawTasksRef.current = updateList(rawTasksRef.current);
  }
  if (ownedTasksRef) {
    ownedTasksRef.current = updateList(ownedTasksRef.current);
  }
}

/**
 * Atomically updates or appends multiple mutated tasks in `rawTasksRef` and `ownedTasksRef`.
 */
export function syncMultipleTasksInRawRefs(
  updatedTasks: Task[],
  rawTasksRef?: React.MutableRefObject<Task[]>,
  ownedTasksRef?: React.MutableRefObject<Task[]>,
  appendIfMissing = true
): void {
  if (updatedTasks.length === 0) return;
  for (const task of updatedTasks) {
    syncTaskInRawRefs(task, task.projectId, rawTasksRef, ownedTasksRef, appendIfMissing);
  }
}

/**
 * Replaces all tasks belonging to `projectId` in `rawTasksRef` and `ownedTasksRef`.
 */
export function replaceProjectTasksInRawRefs(
  projectId: string,
  newTasks: Task[],
  rawTasksRef?: React.MutableRefObject<Task[]>,
  ownedTasksRef?: React.MutableRefObject<Task[]>
): void {
  if (rawTasksRef) {
    rawTasksRef.current = [
      ...rawTasksRef.current.filter((t) => !areProjectIdsMatching(t.projectId, projectId)),
      ...newTasks,
    ];
  }
  if (ownedTasksRef) {
    ownedTasksRef.current = [
      ...ownedTasksRef.current.filter((t) => !areProjectIdsMatching(t.projectId, projectId)),
      ...newTasks,
    ];
  }
}

const recentlyDeletedTimersBySet = new WeakMap<
  Set<string>,
  Map<string, ReturnType<typeof setTimeout>>
>();

function getTimerMapForSet(set: Set<string>): Map<string, ReturnType<typeof setTimeout>> {
  let map = recentlyDeletedTimersBySet.get(set);
  if (!map) {
    map = new Map<string, ReturnType<typeof setTimeout>>();
    recentlyDeletedTimersBySet.set(set, map);
  }
  return map;
}

/**
 * Marks task IDs as recently deleted (with automatic TTL cleanup) so incoming stale Firestore
 * snapshots cannot resurrect deleted tasks during the sync window.
 * Cancels any pre-existing timer for the same task ID to prevent premature expiration after Undo+Redo.
 */
export function recordRecentlyDeletedTaskIds(
  taskIds: Iterable<string | number>,
  recentlyDeletedTaskIdsRef?: React.MutableRefObject<Set<string>>,
  ttlMs = 15000
): void {
  if (!recentlyDeletedTaskIdsRef) return;
  const targetSet = recentlyDeletedTaskIdsRef.current;
  const ids = Array.from(taskIds)
    .map((id) => String(id).trim())
    .filter(Boolean);
  if (ids.length === 0) return;

  const timerMap = getTimerMapForSet(targetSet);

  ids.forEach((idStr) => {
    const existingTimer = timerMap.get(idStr);
    if (existingTimer !== undefined) {
      clearTimeout(existingTimer);
    }
    targetSet.add(idStr);
    const timer = setTimeout(() => {
      timerMap.delete(idStr);
      targetSet.delete(idStr);
    }, ttlMs);
    timerMap.set(idStr, timer);
  });
}

/**
 * Removes task IDs from `recentlyDeletedTaskIdsRef` and cancels their pending TTL timers
 * (used when restoring tasks via Undo).
 */
export function clearRecentlyDeletedTaskIds(
  taskIds: Iterable<string | number>,
  recentlyDeletedTaskIdsRef?: React.MutableRefObject<Set<string>>
): void {
  if (!recentlyDeletedTaskIdsRef) return;
  const targetSet = recentlyDeletedTaskIdsRef.current;
  const timerMap = recentlyDeletedTimersBySet.get(targetSet);
  for (const id of taskIds) {
    const idStr = String(id).trim();
    if (idStr) {
      if (timerMap) {
        const existingTimer = timerMap.get(idStr);
        if (existingTimer !== undefined) {
          clearTimeout(existingTimer);
          timerMap.delete(idStr);
        }
      }
      targetSet.delete(idStr);
    }
  }
}

/**
 * Marks task IDs as dirty in `dirtyTaskIdsRef`.
 */
export function recordDirtyTaskIds(
  taskIds: Iterable<string | number>,
  dirtyTaskIdsRef?: React.MutableRefObject<Set<string>>
): void {
  if (!dirtyTaskIdsRef) return;
  for (const id of taskIds) {
    const idStr = String(id).trim();
    if (idStr) {
      dirtyTaskIdsRef.current.add(idStr);
    }
  }
}

export interface ApplySnapshotTransitionOptions {
  currentState: FullProject[];
  targetState: FullProject[];
  syncTracker?: ProjectSyncTracker;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  markProjectDirty: (id: string) => void;
  markTaskDirty?: (taskId: string | number, projectId?: string) => void;
}

/**
 * Transitions project state during a snapshot Undo/Redo while synchronizing
 * `dirtyTaskIdsRef`, `recentlyDeletedTaskIdsRef`, `rawTasksRef`, and `ownedTasksRef`
 * so incoming Cloud snapshots cannot overwrite or resurrect undone/redone tasks.
 */
export function applyProjectSnapshotTransition({
  currentState,
  targetState,
  syncTracker,
  allProjectsRef,
  setAllProjects,
  markProjectDirty,
  markTaskDirty,
}: ApplySnapshotTransitionOptions): FullProject[] {
  const now = Date.now();
  const restoredTaskIds: string[] = [];
  const dirtyTaskIds: string[] = [];
  const tasksToSyncInRawRefs: Task[] = [];
  const modifiedProjectIds = new Set<string>();
  const targetAllTaskIds = new Set<string>();

  const nextTargetState = targetState.map((p) => {
    const currentP = currentState.find((c) => c.id === p.id);
    const currentTaskMap = new Map<string, Task>();
    (currentP?.tasks || []).forEach((ct) => currentTaskMap.set(String(ct.id).trim(), ct));

    let projectTasksChanged = false;
    const nextTasks = (p.tasks || []).map((t) => {
      const idStr = String(t.id).trim();
      if (idStr) targetAllTaskIds.add(idStr);
      const prevTask = currentTaskMap.get(idStr);
      if (!prevTask) {
        projectTasksChanged = true;
        restoredTaskIds.push(idStr);
        dirtyTaskIds.push(idStr);
        const stamped = { ...t, updatedAt: now };
        tasksToSyncInRawRefs.push(stamped);
        markTaskDirty?.(t.id, p.id);
        return stamped;
      }
      if (prevTask !== t) {
        projectTasksChanged = true;
        dirtyTaskIds.push(idStr);
        const stamped = { ...t, updatedAt: Math.max(t.updatedAt || 0, now) };
        tasksToSyncInRawRefs.push(stamped);
        markTaskDirty?.(t.id, p.id);
        return stamped;
      }
      return t;
    });

    const currentTaskCount = (currentP?.tasks || []).length;
    if (
      !currentP ||
      projectTasksChanged ||
      currentTaskCount !== nextTasks.length ||
      currentP.metadata !== p.metadata
    ) {
      modifiedProjectIds.add(p.id);
      markProjectDirty(p.id);
      return projectTasksChanged ? { ...p, tasks: nextTasks, lastModified: now } : p;
    }
    return p;
  });

  const removedTaskIds: string[] = [];
  currentState.forEach((cp) => {
    (cp.tasks || []).forEach((ct) => {
      const idStr = String(ct.id).trim();
      if (idStr && !targetAllTaskIds.has(idStr)) {
        removedTaskIds.push(idStr);
      }
    });
  });

  if (syncTracker) {
    if (restoredTaskIds.length > 0) {
      syncTracker.clearRecentlyDeleted(restoredTaskIds);
    }
    if (removedTaskIds.length > 0) {
      syncTracker.recordRecentlyDeleted(removedTaskIds);
      const removedSet = new Set(removedTaskIds);
      syncTracker.setRawAndOwnedTasks(
        syncTracker.getRawTasks().filter((rt) => !removedSet.has(String(rt.id).trim())),
        syncTracker.getOwnedTasks().filter((ot) => !removedSet.has(String(ot.id).trim()))
      );
    }
    if (dirtyTaskIds.length > 0) {
      syncTracker.recordDirtyTasks(dirtyTaskIds);
    }
    if (tasksToSyncInRawRefs.length > 0) {
      syncTracker.syncMultipleTasks(tasksToSyncInRawRefs, true);
    }
    syncTracker.commitProjects(nextTargetState, setAllProjects, modifiedProjectIds);
  } else {
    allProjectsRef.current = nextTargetState;
    setAllProjects(nextTargetState);
  }

  return nextTargetState;
}

/**
 * Factory creating an encapsulated `ProjectSyncTracker` instance that bundles all 6 synchronization
 * refs behind atomic domain operations. Eliminates the "Ref-Zoo" across mutation hooks.
 */
export function createProjectSyncTracker(refs: ProjectSyncRefs): ProjectSyncTracker {
  return {
    refs,
    getAllProjects() {
      return refs.allProjectsRef.current;
    },
    setAllProjectsRef(projects: FullProject[]) {
      refs.allProjectsRef.current = projects;
    },
    getRawTasks() {
      return refs.rawTasksRef?.current || [];
    },
    getOwnedTasks() {
      return refs.ownedTasksRef?.current || [];
    },
    setRawAndOwnedTasks(rawTasks: Task[], ownedTasks: Task[]) {
      if (refs.rawTasksRef) {
        refs.rawTasksRef.current = rawTasks;
      }
      if (refs.ownedTasksRef) {
        refs.ownedTasksRef.current = ownedTasks;
      }
    },
    commitProjects(updatedProjects, setAllProjects, modifiedProjectIds) {
      if (modifiedProjectIds) {
        syncModifiedProjectsInRawRef(updatedProjects, modifiedProjectIds, refs.rawProjectsRef);
      } else {
        syncAllProjectsInRawRef(updatedProjects, refs.rawProjectsRef);
      }
      refs.allProjectsRef.current = updatedProjects;
      setAllProjects(updatedProjects);
    },
    syncModifiedProjects(updatedProjects, modifiedProjectIds) {
      syncModifiedProjectsInRawRef(updatedProjects, modifiedProjectIds, refs.rawProjectsRef);
    },
    syncAllProjects(updatedProjects) {
      syncAllProjectsInRawRef(updatedProjects, refs.rawProjectsRef);
    },
    syncTask(updatedTask, projectId, appendIfMissing = false) {
      syncTaskInRawRefs(updatedTask, projectId, refs.rawTasksRef, refs.ownedTasksRef, appendIfMissing);
    },
    syncMultipleTasks(updatedTasks, appendIfMissing = true) {
      syncMultipleTasksInRawRefs(updatedTasks, refs.rawTasksRef, refs.ownedTasksRef, appendIfMissing);
    },
    replaceProjectTasks(projectId, newTasks) {
      replaceProjectTasksInRawRefs(projectId, newTasks, refs.rawTasksRef, refs.ownedTasksRef);
    },
    recordRecentlyDeleted(taskIds, ttlMs = 15000) {
      recordRecentlyDeletedTaskIds(taskIds, refs.recentlyDeletedTaskIdsRef, ttlMs);
    },
    clearRecentlyDeleted(taskIds) {
      clearRecentlyDeletedTaskIds(taskIds, refs.recentlyDeletedTaskIdsRef);
    },
    recordDirtyTasks(taskIds) {
      recordDirtyTaskIds(taskIds, refs.dirtyTaskIdsRef);
    },
  };
}
